import type { Params } from '@vimcar/mvvm/linkViewModel'
import type { CustomAttributeValue, Nodes } from '#models/Nodes'
import type { CheckboxesNode } from '#models/Nodes/CheckboxesNode'

import logger from '@vimcar/shared-utils/logger'
import * as mobx from 'mobx'
import PT from 'prop-types'
import { Observable } from 'rxjs/Observable'
import * as o from 'rxjs/operators'

import type { ComplexNode } from '../../models/Nodes/ComplexNode'
import type { CustomNode } from '../../models/Nodes/CustomNode'
import type { SelectNode } from '../../models/Nodes/SelectNode'
import type { ValuedNode } from '../../models/Nodes/ValuedNode'
import type { Transitioner } from '../TransitionProvider'

import {
  ATTRIBUTE_CLIENTID_CLIENT,
  ATTRIBUTE_USERID_CLIENT,
  ATTRIBUTE_NAME_CLIENT,
  ATTRIBUTE_EMAIL_CLIENT,
} from '../../utils/constants'

export type ViewState = {
  currentNode:
    | SelectNode
    | CustomNode
    | ValuedNode
    | ComplexNode
    | CheckboxesNode
    | null
  nextNode:
    | SelectNode
    | CustomNode
    | ValuedNode
    | ComplexNode
    | CheckboxesNode
    | null
  // @ts-expect-error@2314
  customAttributes: mobx.ObservableMap<string, CustomAttributeValue>
  isComplexNode: boolean
  customAttributesObject: Record<string, unknown>
}

export type ViewModel = {
  state: ViewState
  showSelectNode: boolean
  showCheckboxesNode: boolean
  showComplexNode: boolean
  customNodeName?: string | null
}

type Context = {
  models: { nodes: Nodes }
  transitioner: Transitioner
}

const mkViewModel = (
  { registerObservable, registerAutorun, disposer }: Params,
  _props: unknown,
  { models: { nodes }, transitioner }: Context,
) => {
  const vm = mobx.observable({
    state: {
      currentNode: null,
      nextNode: null,
      customAttributes: mobx.observable.map(),

      get isComplexNode() {
        return !!(
          vm.state.currentNode &&
          vm.state.currentNode.contentModelType === 'complexNode'
        )
      },

      get customAttributesObject() {
        const stateValues = {}
        vm.state.customAttributes.keys().forEach((key) => {
          stateValues[key] = vm.state.customAttributes.get(key)
        })
        vm.clientAttributes.keys().forEach((key) => {
          stateValues[key] = vm.clientAttributes.get(key)
        })
        return stateValues
      },
    },

    get showSelectNode() {
      return !!(
        vm.state.currentNode &&
        vm.state.currentNode.contentModelType === 'selectNode'
      )
    },

    get showCheckboxesNode() {
      return !!(
        vm.state.currentNode &&
        vm.state.currentNode.contentModelType === 'checkboxesNode'
      )
    },

    get showComplexNode() {
      return vm.state.isComplexNode
    },

    get customNodeName() {
      return (
        !!(
          vm.state.currentNode &&
          vm.state.currentNode.contentModelType === 'customNode'
        ) && vm.state.currentNode.customType
      )
    },

    get clientAttributes() {
      const clientAttributesObject = mobx.observable.map()
      const attrs = transitioner.getAttributes()
      if (attrs) {
        clientAttributesObject.set(ATTRIBUTE_CLIENTID_CLIENT, attrs.client_id)
        clientAttributesObject.set(ATTRIBUTE_USERID_CLIENT, attrs.user.id)
        clientAttributesObject.set(ATTRIBUTE_NAME_CLIENT, attrs.user.fullName)
        clientAttributesObject.set(ATTRIBUTE_EMAIL_CLIENT, attrs.user.email)
      }
      return clientAttributesObject
    },
  })

  registerAutorun(() => {
    registerObservable(
      nodes.fetch('de-DE').pipe(
        o.catchError((err) => {
          logger.error(err)
          return Observable.empty()
        }),
        o.tap(() => {
          vm.state.currentNode = transitioner.getCurrentNode()
        }),
      ),
    )
    // Debugging purposes
    // @ts-expect-error@2339
    window.CURRENT_DATA = {
      get: () => {
        const valueCollection = transitioner.getValueCollection()
        const attrs = {}
        valueCollection.attrs.keys().forEach((key) => {
          attrs[key] = valueCollection.attrs.get(key)
        })
        return {
          ...valueCollection,
          attrs,
          customAttributes: vm.state.customAttributesObject,
        }
      },
    }
  })

  disposer.add(
    mobx.reaction(
      () => {
        return transitioner.getCurrentNode()
      },
      (node) => {
        vm.state.currentNode = node
        vm.state.nextNode = null
      },
    ),
  )

  return vm
}

mkViewModel.contextTypes = {
  models: PT.object.isRequired,
  transitioner: PT.object.isRequired,
}

export default mkViewModel
